import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { list, uploadData, remove, getUrl } from "aws-amplify/storage";
import AuthInterceptor from "../../interceptors/authInterceptor.jsx";
import Layout from "../../components/layout/AdminLayout.jsx";

const client = generateClient();

function Devocionario() {
  const [devocionarios, setDevocionarios] = useState([]);
  const [selectedDevocionario, setSelectedDevocionario] = useState(null);
  const [images, setImages] = useState([{ path: "", url: "" }]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [name, setName] = useState("");
  const [pagesFolder, setPagesFolder] = useState("");
  const [loading, setLoading] = useState(false);

  // Obtener la lista de devocionarios
  useEffect(() => {
    const fetchDevocionarios = async () => {
      try {
        const { data: items } = await client.models.Devocionario.list({
          authMode: "userPool",
        });
        await setDevocionarios(items);
      } catch (error) {
        console.error("Error al obtener devocionarios:", error);
      }
    };

    fetchDevocionarios();
  }, []);

  // Obtener imágenes de la carpeta seleccionada
  const fetchImages = async (folderPath) => {
    try {
      setLoading(true);
      const filesResult = await list({ path: `devocionario/${folderPath}` });
      const files = Array.isArray(filesResult.items) ? filesResult.items : [];
      const imageUrls = await Promise.all(
        files.map(async (file) => {
          const { url } = await getUrl({ path: file.path });
          return {
            path: file.path,
            url: url.href,
          };
        }),
      );
      await setImages(imageUrls);
    } catch (error) {
      console.error("Error al obtener imágenes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección de devocionario
  const handleSelectDevocionario = async (devocionario) => {
    await setSelectedDevocionario(devocionario);
    await fetchImages(devocionario.pagesFolder);
  };

  // Subir imágenes a la carpeta
  const handleUploadImages = async () => {
    if (!selectedDevocionario) {
      alert("Selecciona un devocionario primero.");
      return;
    }
    const folderPath = selectedDevocionario.pagesFolder;
    try {
      setLoading(true);

      // Crear un array de promesas para subir las imágenes
      const uploadPromises = newImageFiles.map(async (file) => {
        const filePath = `devocionario/${folderPath}/${file.name}`;
        await uploadData({ path: filePath, data: file }).result;
        const { url } = await getUrl({ path: filePath });
        return { path: filePath, url: url.href };
      });

      // Esperar a que todas las imágenes se suban
      const newImages = await Promise.all(uploadPromises);
      await setImages([...images, ...newImages]);
      await setNewImageFiles([]);
    } catch (error) {
      console.error("Error al subir imágenes:", error);
      alert("Error al subir imágenes.");
    } finally {
      setLoading(false);
    }
  };

  // Eliminar una imagen
  const handleDeleteImage = async (path) => {
    if (window.confirm("¿Seguro que deseas eliminar esta imagen?")) {
      try {
        await remove({ path });
        await fetchImages(selectedDevocionario.pagesFolder);
      } catch (error) {
        console.error("Error al eliminar imagen:", error);
        alert("Error al eliminar la imagen.");
      }
    }
  };

  // Crear un nuevo devocionario
  const handleCreateDevocionario = async (e) => {
    e.preventDefault();
    try {
      const { data: items } = await client.models.Devocionario.create(
        {
          name,
          pagesFolder,
        },
        {
          authMode: "userPool",
        },
      );
      await setDevocionarios([...devocionarios, items]);
      await setName("");
      await setPagesFolder("");
    } catch (error) {
      console.error("Error al crear devocionario:", error);
      alert("Error al crear el devocionario.");
    }
  };

  return (
    <Layout>
      <AuthInterceptor>
        <div className="min-h-screen bg-gray-100 p-4">
          <div className="w-full rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-6 text-center text-2xl font-bold text-gray-800">
              Gestión de Devocionarios
            </h2>

            {/* Formulario para agregar un nuevo devocionario */}
            <form
              onSubmit={handleCreateDevocionario}
              className="mb-6 rounded-lg bg-slate-50 p-6 shadow-lg"
            >
              <h3 className="mb-4 text-lg font-semibold text-gray-700">
                Agregar Nuevo Devocionario
              </h3>
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Nombre
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Carpeta de Imágenes (S3)
                </label>
                <input
                  type="text"
                  value={pagesFolder}
                  onChange={(e) => setPagesFolder(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                className="rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white shadow-md hover:bg-indigo-700"
              >
                Crear
              </button>
            </form>

            {/* Lista de devocionarios */}
            <div className="mb-6 rounded-lg bg-slate-50 p-6 shadow-lg">
              <h3 className="mb-4 text-lg font-semibold text-gray-700">
                Lista de Devocionarios
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {devocionarios &&
                  devocionarios.map((devocionario) => (
                    <div
                      key={devocionario.id}
                      className={`cursor-pointer rounded-lg bg-white p-4 shadow ${
                        selectedDevocionario?.id === devocionario.id
                          ? "border border-indigo-500"
                          : ""
                      }`}
                      onClick={() => handleSelectDevocionario(devocionario)}
                    >
                      <h4 className="font-semibold text-gray-800">
                        {devocionario.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {devocionario.pagesFolder}
                      </p>
                    </div>
                  ))}
              </div>
              {/* Imágenes del devocionario seleccionado */}
              {selectedDevocionario && (
                <div className="my-6">
                  {loading ? (
                    <p className="text-gray-700">Cargando imágenes...</p>
                  ) : (
                    images.length > 0 && (
                      <>
                        <h3 className="mb-4 text-lg font-semibold text-gray-700">
                          Imágenes Públicas del Devocionario:{" "}
                          {selectedDevocionario.name}
                        </h3>
                        <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                          {images.map((image) => (
                            <div key={image.path} className="group relative">
                              <img
                                src={image.url}
                                alt="Imagen"
                                className="h-40 w-full rounded-lg object-cover shadow"
                              />
                              <button
                                onClick={() => handleDeleteImage(image.path)}
                                className="absolute right-2 top-2 rounded bg-red-600 px-2 py-1 text-xs text-white opacity-0 shadow group-hover:opacity-100"
                              >
                                Eliminar
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    )
                  )}
                </div>
              )}
              {/* Imágenes del devocionario seleccionado */}
              {selectedDevocionario && (
                <div className="mb-6">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Subir Imágenes
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => setNewImageFiles([...e.target.files])}
                      className="block w-full cursor-pointer rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    {newImageFiles.length > 0 && (
                      <button
                        onClick={handleUploadImages}
                        disabled={newImageFiles.length === 0}
                        className="mt-4 rounded-lg bg-green-600 px-4 py-2 font-semibold text-white shadow-md hover:bg-green-700"
                      >
                        Subir Imágenes
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </AuthInterceptor>
    </Layout>
  );
}

export default Devocionario;
